// Device guides for IPTV setup with comprehensive setup steps, specifications, and FAQs
export const deviceGuides = {
  firestick: {
    id: 'firestick',
    name: 'Amazon Firestick',
    variants: ['Firestick 4K', 'Firestick 4K Max', 'Firestick HD', 'Fire TV Stick 3rd Gen'],
    category: 'Streaming Box',
    overview: 'Amazon Firestick is the most popular IPTV device with exceptional app compatibility and performance.',
    specifications: {
      processor: 'Quad-core 1.7 GHz',
      ram: '2 GB DDR3',
      storage: '8 GB / 16 GB options',
      resolution: '4K @ 60fps (4K Max), 1080p (HD)',
      connectivity: 'WiFi 5 (WiFi 6 on Max), Ethernet adapter available',
      compatibility: 'TiviMate, IPTV Smarters, GSE Smart IPTV, VLC'
    },
    setupSteps: [
      {
        step: 1,
        title: 'Enable Unknown Sources',
        description: 'Settings → Applications → Unknown Sources → Enable',
        time: '1 min'
      },
      {
        step: 2,
        title: 'Download Downloader App',
        description: 'Search "Downloader" in Fire TV app store, install official Downloader by AFTVnews',
        time: '2 min'
      },
      {
        step: 3,
        title: 'Get APK Download Link',
        description: 'Use Downloader to access IPTV player APK (TiviMate recommended: tivimate.com)',
        time: '3 min'
      },
      {
        step: 4,
        title: 'Install IPTV Player',
        description: 'Downloader will fetch and install APK automatically',
        time: '2 min'
      },
      {
        step: 5,
        title: 'Enter Credentials',
        description: 'Launch app → Enter M3U playlist or Xtream Codes credentials',
        time: '2 min'
      },
      {
        step: 6,
        title: 'Test Streaming',
        description: 'Browse channels, select favorite, verify playback quality',
        time: '5 min'
      }
    ],
    pros: ['Most popular device', 'Best app selection', 'Affordable ($55)', 'Excellent performance', '4K support'],
    cons: ['APK installation slightly complex for non-tech users', 'Fire TV OS restrictions', 'Limited customization'],
    recommendedApps: ['TiviMate ($5.99)', 'GSE Smart IPTV ($14.99)', 'IPTV Smarters (free)'],
    troubleshooting: [
      {
        problem: 'App won\'t install from Downloader',
        solution: 'Ensure unknown sources enabled, restart Firestick, retry download'
      },
      {
        problem: 'Buffering or freezing',
        solution: 'Use wired ethernet adapter, reduce resolution, switch IPTV server'
      },
      {
        problem: 'No channels loading',
        solution: 'Verify credentials correct, check internet speed (10+ Mbps), try different server'
      }
    ],
    faq: [
      { q: 'Is Firestick best for IPTV?', a: 'Yes, it\'s most popular and app-compatible choice' },
      { q: 'Can I sideload apps?', a: 'Yes, via Downloader app (very easy process)' },
      { q: 'Does 4K require better specs?', a: 'Yes, Firestick 4K Max best for 4K streaming' }
    ]
  },
  roku: {
    id: 'roku',
    name: 'Roku Streaming Device',
    variants: ['Roku Streaming Stick 4K', 'Roku Ultra', 'Roku Express 4K'],
    category: 'Streaming Box',
    overview: 'Roku provides excellent streaming quality but has limited native IPTV app support, requiring workarounds.',
    specifications: {
      processor: 'Dual-core processor',
      ram: '2 GB DDR3',
      storage: 'Flash storage',
      resolution: '4K @ 60fps',
      connectivity: 'WiFi 5, Ethernet available',
      compatibility: 'Flix IPTV (M3U), Screen mirroring, AirPlay'
    },
    setupSteps: [
      {
        step: 1,
        title: 'Install Flix IPTV',
        description: 'Roku Channel Store → Search "Flix IPTV" → Install',
        time: '2 min'
      },
      {
        step: 2,
        title: 'Enter M3U URL',
        description: 'Launch Flix IPTV → Settings → Playlist URL → paste M3U link',
        time: '2 min'
      },
      {
        step: 3,
        title: 'Load Channels',
        description: 'App loads playlist automatically, browse available channels',
        time: '1 min'
      },
      {
        step: 4,
        title: 'Enable Screen Mirroring (Alternative)',
        description: 'Settings → Remotes & devices → Screen mirroring → Enable',
        time: '1 min'
      },
      {
        step: 5,
        title: 'Cast IPTV from Phone',
        description: 'Use phone IPTV app → Cast button → select Roku device',
        time: '1 min'
      }
    ],
    pros: ['Simple setup', 'Good performance', 'Affordable', 'Wide app selection for non-IPTV'],
    cons: ['Limited native IPTV apps', 'Workarounds required', 'M3U-only no Xtream Codes'],
    recommendedApps: ['Flix IPTV (free)', 'Screen mirroring from phone'],
    troubleshooting: [
      {
        problem: 'Flix IPTV not available in channel store',
        solution: 'Check Roku account region, may be region-restricted'
      },
      {
        problem: 'M3U URL not loading',
        solution: 'Verify URL correct, check internet connection, try different M3U source'
      }
    ],
    faq: [
      { q: 'Why limited IPTV support?', a: 'Roku restricts app store submissions for IPTV apps' },
      { q: 'Best workaround?', a: 'Screen mirroring from smartphone or Flix IPTV app' },
      { q: 'Can I use TiviMate on Roku?', a: 'No, TiviMate Android app not available on Roku' }
    ]
  },
  samsungTV: {
    id: 'samsung-tv',
    name: 'Samsung Smart TV',
    variants: ['Samsung QLED', 'Samsung Crystal UHD', 'Samsung Frame TV', 'Samsung The Sero'],
    category: 'Smart TV',
    overview: 'Samsung Smart TVs with Tizen OS support excellent native IPTV app installation with excellent performance.',
    specifications: {
      processor: 'Samsung Tizen processor (varies by model)',
      ram: '2-4 GB DDR3',
      storage: '8-16 GB flash',
      resolution: '4K @ 60fps',
      connectivity: 'WiFi 5/6, Ethernet',
      compatibility: 'IPTV Smarters, GSE Smart IPTV, TiviMate (limited)'
    },
    setupSteps: [
      {
        step: 1,
        title: 'Open Samsung App Store',
        description: 'TV remote → Samsung Apps → Search',
        time: '1 min'
      },
      {
        step: 2,
        title: 'Download IPTV Player',
        description: 'Search "IPTV Smarters" or "GSE Smart IPTV" → Install',
        time: '2 min'
      },
      {
        step: 3,
        title: 'Launch App',
        description: 'App auto-launches or select from home screen',
        time: '1 min'
      },
      {
        step: 4,
        title: 'Enter Credentials',
        description: 'First launch prompts for M3U or Xtream Codes credentials',
        time: '2 min'
      },
      {
        step: 5,
        title: 'Configure Settings',
        description: 'Quality, playback, parental controls, subtitle language',
        time: '3 min'
      },
      {
        step: 6,
        title: 'Test Streaming',
        description: 'Select channel, verify HD/4K quality, check audio'
        , time: '5 min'
      }
    ],
    pros: ['Native app support', 'Excellent performance', 'No workarounds needed', '4K support', 'Large selection of apps'],
    cons: ['App availability varies by region', 'Some models limited compatibility'],
    recommendedApps: ['IPTV Smarters Pro ($14.99)', 'GSE Smart IPTV ($14.99)'],
    troubleshooting: [
      {
        problem: 'App not found in Samsung App Store',
        solution: 'Try alternative app (GSE vs IPTV Smarters), check region restrictions'
      },
      {
        problem: 'Buffering on TV app',
        solution: 'Use ethernet connection, reduce resolution to 1080p, check WiFi signal'
      }
    ],
    faq: [
      { q: 'Is Samsung TV best for native IPTV?', a: 'Yes, Samsung native apps work excellently' },
      { q: 'Which app is best?', a: 'IPTV Smarters Pro most popular and reliable' },
      { q: 'Can I use without subscription?', a: 'No, requires IPTV subscription and valid credentials' }
    ]
  },
  lgTV: {
    id: 'lg-tv',
    name: 'LG Smart TV',
    variants: ['LG OLED', 'LG NanoCell', 'LG QNED', 'LG UltraHD'],
    category: 'Smart TV',
    overview: 'LG Smart TVs running WebOS provide excellent native IPTV app support with great performance.',
    specifications: {
      processor: 'LG WebOS processor',
      ram: '2-4 GB DDR3',
      storage: '8-16 GB flash',
      resolution: '4K @ 60fps',
      connectivity: 'WiFi 5/6, Ethernet',
      compatibility: 'IPTV Smarters, GSE Smart IPTV, WebOS native'
    },
    setupSteps: [
      {
        step: 1,
        title: 'Access LG App Store',
        description: 'TV home button → LG Content Store',
        time: '1 min'
      },
      {
        step: 2,
        title: 'Search IPTV App',
        description: 'Search "IPTV Smarters" or "GSE Smart IPTV"',
        time: '1 min'
      },
      {
        step: 3,
        title: 'Install Application',
        description: 'Select app → Install (may require LG account)',
        time: '2 min'
      },
      {
        step: 4,
        title: 'Launch App',
        description: 'App appears on home screen or launch from App Store',
        time: '1 min'
      },
      {
        step: 5,
        title: 'Add Credentials',
        description: 'First launch prompt → M3U playlist URL or Xtream codes',
        time: '2 min'
      },
      {
        step: 6,
        title: 'Enjoy Streaming',
        description: 'Browse channels, test quality, adjust settings as needed',
        time: '5 min'
      }
    ],
    pros: ['Native WebOS apps', 'Excellent picture quality on OLED', 'Wide app selection', 'Reliable performance'],
    cons: ['LG account sometimes required', 'Regional app availability varies'],
    recommendedApps: ['IPTV Smarters Pro ($14.99)', 'GSE Smart IPTV ($14.99)'],
    troubleshooting: [
      {
        problem: 'App installation fails',
        solution: 'Ensure LG account logged in, check internet connection, restart TV'
      },
      {
        problem: 'Poor performance with 4K',
        solution: 'Switch to 1080p, use ethernet, reduce simultaneous apps'
      }
    ],
    faq: [
      { q: 'Is LG better than Samsung for IPTV?', a: 'Both excellent, WebOS very smooth performance' },
      { q: 'Do I need LG subscription?', a: 'No, only IPTV provider subscription required' },
      { q: 'Can I return to regular TV after setup?', a: 'Yes, easily switch between IPTV and standard apps' }
    ]
  },
  appleTV: {
    id: 'apple-tv',
    name: 'Apple TV',
    variants: ['Apple TV 4K (3rd gen)', 'Apple TV HD'],
    category: 'Streaming Box',
    overview: 'Apple TV provides the most seamless Apple ecosystem integration with excellent App Store IPTV options.',
    specifications: {
      processor: 'A12 Bionic (4K) / A10X Fusion (HD)',
      ram: '3-4 GB LPDDR4',
      storage: '32 GB / 64 GB options',
      resolution: '4K @ 60fps',
      connectivity: 'WiFi 6, Ethernet, AirPlay',
      compatibility: 'IPTV Smarters, GSE Smart IPTV'
    },
    setupSteps: [
      {
        step: 1,
        title: 'Access App Store',
        description: 'Apple TV home → App Store icon',
        time: '1 min'
      },
      {
        step: 2,
        title: 'Search IPTV App',
        description: 'Search tab → "IPTV Smarters" or "GSE Smart IPTV"',
        time: '1 min'
      },
      {
        step: 3,
        title: 'Download & Install',
        description: 'Select app → Get → Install (uses Apple ID)',
        time: '2 min'
      },
      {
        step: 4,
        title: 'Launch Application',
        description: 'App appears on home screen, select to launch',
        time: '1 min'
      },
      {
        step: 5,
        title: 'Enter IPTV Credentials',
        description: 'M3U URL or Xtream Codes credentials',
        time: '2 min'
      },
      {
        step: 6,
        title: 'Adjust Settings',
        description: 'Resolution (1080p/4K), audio output, subtitles',
        time: '3 min'
      }
    ],
    pros: ['Official App Store (legitimate)', 'Seamless Apple ecosystem', 'Excellent performance', 'Easy setup'],
    cons: ['Most expensive option ($129+)', 'Limited to App Store availability', 'Smaller app selection than Android'],
    recommendedApps: ['IPTV Smarters Pro ($14.99)', 'GSE Smart IPTV ($14.99)'],
    troubleshooting: [
      {
        problem: 'App not in App Store for your region',
        solution: 'Change App Store region, use VPN, or sideload via Xcode'
      },
      {
        problem: 'AirPlay streaming laggy',
        solution: 'Use direct app installation, ensure strong WiFi (WiFi 6 preferred)'
      }
    ],
    faq: [
      { q: 'Is Apple TV worth the cost?', a: 'Yes if you have Apple ecosystem, best experience' },
      { q: 'Can I use IPTV without Apple ID?', a: 'No, Apple ID required to install apps' },
      { q: '4K playback lag?', a: 'Apple TV 4K handles 4K IPTV excellently' }
    ]
  },
  androidTV: {
    id: 'android-tv',
    name: 'Android TV / Google TV',
    variants: ['Generic Android TV boxes', 'Google Chromecast with Google TV', 'Android TV by manufacturer'],
    category: 'Streaming Box / TV Platform',
    overview: 'Android TV and Google TV platforms provide maximum flexibility with extensive app selection.',
    specifications: {
      processor: 'Quad-core MediaTek / Snapdragon',
      ram: '2-4 GB DDR3/4',
      storage: '8-16 GB eMMC',
      resolution: '4K @ 60fps',
      connectivity: 'WiFi 5/6, Ethernet (boxesdependent)',
      compatibility: 'TiviMate, IPTV Smarters, GSE Smart IPTV, VLC'
    },
    setupSteps: [
      {
        step: 1,
        title: 'Open Play Store',
        description: 'Home → Apps or Google Play Store app',
        time: '1 min'
      },
      {
        step: 2,
        title: 'Search IPTV App',
        description: 'Search "TiviMate" or "IPTV Smarters"',
        time: '1 min'
      },
      {
        step: 3,
        title: 'Install Application',
        description: 'Install button → Wait for completion',
        time: '2 min'
      },
      {
        step: 4,
        title: 'Launch App',
        description: 'Open app from Play Store or home screen',
        time: '1 min'
      },
      {
        step: 5,
        title: 'Configure Credentials',
        description: 'Enter M3U playlist or Xtream Codes details',
        time: '2 min'
      },
      {
        step: 6,
        title: 'Customize Settings',
        description: 'Quality, playback options, theme preferences',
        time: '3 min'
      }
    ],
    pros: ['Maximum app selection', 'Affordable ($30-100)', 'Flexible customization', 'Open ecosystem'],
    cons: ['Quality varies by device', 'Requires Google account', 'Some boxes underpowered'],
    recommendedApps: ['TiviMate ($5.99)', 'IPTV Smarters Pro ($14.99)', 'GSE Smart IPTV ($14.99)'],
    troubleshooting: [
      {
        problem: 'Buffering on cheaper Android boxes',
        solution: 'Upgrade to better device, use wired ethernet, lower resolution'
      },
      {
        problem: 'Google Play Store apps limited in region',
        solution: 'Use APK downloads, change device region, use VPN temporarily'
      }
    ],
    faq: [
      { q: 'Best budget Android TV box?', a: 'Google Chromecast with Google TV $50-60' },
      { q: 'Can I install APKs directly?', a: 'Yes, enable unknown sources, sideload APK files' },
      { q: 'Performance reliable?', a: 'Yes on quality devices, varies on budget boxes' }
    ]
  },
  windows: {
    id: 'windows',
    name: 'Windows PC / Laptop',
    variants: ['Windows 10', 'Windows 11', 'Windows Server 2019/2022'],
    category: 'Computer',
    overview: 'Windows PCs offer flexible IPTV streaming via multiple applications with excellent performance.',
    specifications: {
      processor: 'Any Intel/AMD processor',
      ram: '4 GB minimum (8 GB recommended)',
      storage: 'Minimal (apps are lightweight)',
      resolution: '1080p minimum (4K capable)',
      connectivity: 'WiFi or ethernet',
      compatibility: 'VLC, Kodi, TiviMate, MPC-HC'
    },
    setupSteps: [
      {
        step: 1,
        title: 'Download VLC Media Player',
        description: 'Visit vlc.org, download Windows installer',
        time: '2 min'
      },
      {
        step: 2,
        title: 'Install Application',
        description: 'Run installer, follow setup wizard',
        time: '2 min'
      },
      {
        step: 3,
        title: 'Get M3U Playlist',
        description: 'From IPTV provider, copy M3U URL',
        time: '1 min'
      },
      {
        step: 4,
        title: 'Open M3U in VLC',
        description: 'Media → Open Network Stream → paste M3U URL → Play',
        time: '2 min'
      },
      {
        step: 5,
        title: 'Configure Playback',
        description: 'Quality settings, subtitles, aspect ratio',
        time: '3 min'
      },
      {
        step: 6,
        title: 'Create Shortcut (Optional)',
        description: 'Desktop shortcut for quick access',
        time: '1 min'
      }
    ],
    pros: ['Ultimate flexibility', 'Powerful application options', 'Free options available', 'Best for advanced users'],
    cons: ['Not optimized for TV viewing', 'Requires setup', 'Less convenient than dedicated boxes'],
    recommendedApps: ['VLC (free)', 'Kodi (free)', 'MPC-HC (free)', 'Plex (freemium)'],
    troubleshooting: [
      {
        problem: 'M3U link opens in browser instead of VLC',
        solution: 'Right-click M3U → Open with → Select VLC'
      },
      {
        problem: 'Buffering or stuttering',
        solution: 'Close background apps, upgrade RAM, use wired connection'
      }
    ],
    faq: [
      { q: 'Is Windows better for IPTV than box?', a: 'Flexible but less convenient than dedicated devices' },
      { q: 'Can I cast to TV?', a: 'Yes, via HDMI or wireless casting (DisplayPort/Wireless Display)' },
      { q: 'Free options?', a: 'Yes, VLC and Kodi both excellent free choices' }
    ]
  },
  mac: {
    id: 'mac',
    name: 'macOS (Apple Mac)',
    variants: ['MacBook Pro', 'MacBook Air', 'Mac mini', 'Mac Studio', 'iMac'],
    category: 'Computer',
    overview: 'macOS provides excellent IPTV streaming via native applications with seamless Apple ecosystem integration.',
    specifications: {
      processor: 'Apple Silicon (M1+) or Intel',
      ram: '8 GB minimum (16 GB recommended)',
      storage: 'Minimal (apps lightweight)',
      resolution: '1080p minimum (4K capable on M1+)',
      connectivity: 'WiFi or Thunderbolt ethernet',
      compatibility: 'VLC, Kodi, IPTV Smarters, Plex'
    },
    setupSteps: [
      {
        step: 1,
        title: 'Open App Store',
        description: 'Applications → App Store',
        time: '1 min'
      },
      {
        step: 2,
        title: 'Search IPTV App',
        description: 'Search "IPTV Smarters" or install VLC from web',
        time: '1 min'
      },
      {
        step: 3,
        title: 'Install Application',
        description: 'Get → Install with Apple ID (or download VLC)',
        time: '2 min'
      },
      {
        step: 4,
        title: 'Launch App',
        description: 'Applications → open app',
        time: '1 min'
      },
      {
        step: 5,
        title: 'Add Credentials',
        description: 'M3U URL or Xtream Codes details',
        time: '2 min'
      },
      {
        step: 6,
        title: 'Enjoy on Big Screen',
        description: 'Connect to external display via HDMI or AirPlay',
        time: '5 min'
      }
    ],
    pros: ['Apple ecosystem integration', 'AirPlay support', 'Excellent performance', 'Reliable applications'],
    cons: ['Requires Apple hardware', 'Less convenient than dedicated boxes', 'Higher upfront device cost'],
    recommendedApps: ['IPTV Smarters Pro ($14.99)', 'VLC (free)', 'Kodi (free)', 'Plex (freemium)'],
    troubleshooting: [
      {
        problem: 'M1/M2 Mac app compatibility',
        solution: 'Most apps have Apple Silicon versions, check app page'
      },
      {
        problem: 'AirPlay video lag',
        solution: 'Wired HDMI connection more reliable for 4K'
      }
    ],
    faq: [
      { q: 'Is Mac good for IPTV?', a: 'Yes, excellent option especially for Apple users' },
      { q: 'Can I use Mac mini as set-top box?', a: 'Yes, very popular choice with HDMI output' },
      { q: 'M1/M2 performance good?', a: 'Excellent, handles 4K smoothly' }
    ]
  }
};

export const devicePriority = ['firestick', 'roku', 'samsungTV', 'lgTV', 'appleTV', 'androidTV', 'windows', 'mac'];
